import { useCallback, useEffect, useRef, useState } from 'react';

const SIDE_PANEL_DIMENSIONS = {
	left: {
		default: '25%',
		min: 200,
		max: 600,
	},
	right: {
		default: '25%',
		min: 200,
		max: 600,
	},
};

type useResizablePanelProps = {
	side: 'left' | 'right';
};

export const useResizablePanel = ({ side }: useResizablePanelProps) => {
	const [width, setWidth] = useState(SIDE_PANEL_DIMENSIONS[side].default);
	const isResizing = useRef(false);
	const [windowWidth, setWindowWidth] = useState(
		typeof window !== 'undefined' ? window.innerWidth : 0
	);

	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
		};

		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const handleMouseMove = useCallback(
		(e: MouseEvent) => {
			if (!isResizing.current) {
				return;
			}

			let newWidth;
			if (side === 'left') {
				newWidth = Math.min(
					Math.max(e.clientX, SIDE_PANEL_DIMENSIONS[side].min),
					SIDE_PANEL_DIMENSIONS[side].max
				);
			} else {
				newWidth = Math.min(
					Math.max(windowWidth - e.clientX, SIDE_PANEL_DIMENSIONS[side].min),
					SIDE_PANEL_DIMENSIONS[side].max
				);
			}

			setWidth(`${newWidth}px`);
		},
		[side, windowWidth]
	);

	const handleMouseUp = useCallback(() => {
		isResizing.current = false;
		document.removeEventListener('mousemove', handleMouseMove);
		document.removeEventListener('mouseup', handleMouseUp);
	}, [handleMouseMove]);

	const handleMouseDown = useCallback(() => {
		isResizing.current = true;
		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	}, [handleMouseMove, handleMouseUp]);

	useEffect(() => {
		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [handleMouseMove, handleMouseUp]);

	return { width, handleMouseDown, isResizing };
};
